/**
 * TableHeader — the bar above a data table.
 *
 * Layout: left = title + count badge + (optional) custom actions,
 * right = sort pill + toolbar. The count badge is a quiet grey pill;
 * when `countFiltered` is true it picks up a filter glyph and a brand
 * tint, so a filtered view is visually distinct from the full set at
 * a glance. (It used to be a solid primary-blue fill, the only one on
 * the page, and read as if it belonged to another app.)
 *
 * Kept as a pure presentational primitive — it does not read the
 * MRT `table` instance. The orchestrating Table component wires
 * sort/filter/pagination handlers and passes them in as props.
 *
 * `compact` (boolean) — drops the top padding of the Row for callers
 * that already provide visual breathing room (e.g. when the table sits
 * under a card with its own bottom margin).
 */
import { styled } from '@mui/material/styles';
import { SkeletonBar } from '../Skeleton';
import SortPill from './SortPill';
import TableToolbar from './TableToolbar';

const Row = styled('div', { shouldForwardProp: prop => prop !== 'compact' })(({ compact }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: compact ? '0 32px 12px' : '20px 32px 12px',
  // `flex-wrap: wrap` keeps long title + crowded toolbar readable on
  // narrow viewports without us having to build a mobile layout.
  flexWrap: 'wrap',
}));

// Rendered as a `div` (not `h1`) on purpose: there is a global
// `h1` rule in colors_and_type.css that pushes the font up to the
// page-hero size — the table title needs its own calmer scale.
const Title = styled('div')({
  margin: 0,
  fontFamily: "'Lato', sans-serif",
  fontSize: 22,
  fontWeight: 700,
  color: 'var(--qhs-text-1)',
  letterSpacing: '-0.3px',
  lineHeight: 1.2,
});

const CountBadge = styled('span', {
  shouldForwardProp: prop => prop !== '$filtered',
})(({ $filtered }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  // The filtered tint is the brand colour mixed down, so it follows
  // `--qhs-brand` into dark mode; there is no brand-tint token.
  background: $filtered
    ? 'color-mix(in srgb, var(--qhs-brand) 12%, transparent)'
    : 'var(--qhs-st-inactive-bg)',
  color: $filtered ? 'var(--qhs-brand)' : 'var(--qhs-st-inactive-tx)',
  fontSize: 13,
  fontWeight: 600,
  padding: '2px 10px',
  borderRadius: 999,
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1.4,
}));

const Spacer = styled('div')({ flex: 1 });

const RightGroup = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  flexShrink: 0,
});

const InlineFilterIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="12"
    height="12"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

const formatCount = value => {
  if (value == null) return null;
  try {
    return value.toLocaleString('en-US');
  } catch (err) {
    return String(value);
  }
};

const TableHeader = ({
  title,
  count,
  countFiltered = false,
  countLoading = false,
  // "Filtered by" pill, rendered first in the right group so it reads
  // as a pair with "Sorted by". Built by Table, which owns filter state.
  filterPill,
  // Legacy-style sort API — mirrors the inputs of the original
  // SortSelector: an array of MRT-style sort objects, a setter, and
  // the column schema the pill needs to build its radio list.
  sorting,
  setSorting,
  columns,
  toolbarActions,
  toolbarExtras,
  // Slot rendered to the LEFT of SortPill — for filter pills that are
  // conceptually peers of the sort pill (e.g. an account selector
  // pill). They share SortPill's styling so the row reads as a single
  // group of filter/sort controls.
  leftToolbarExtras,
  titleActions,
  compact = false,
  excludeSortKeys,
}) => {
  const formattedCount = formatCount(count);

  return (
    <Row compact={compact}>
      {title && <Title>{title}</Title>}
      {/* Stands in for the count while the rows show skeletons, so a stale
          or missing number never sits beside a loading table. Badge-sized,
          so the title row does not move when the number lands. */}
      {countLoading && <SkeletonBar variant="rounded" width={56} height={22} radius={999} />}
      {!countLoading && formattedCount != null && (
        <CountBadge $filtered={countFiltered}>
          {countFiltered && <InlineFilterIcon />}
          {formattedCount}
        </CountBadge>
      )}
      {titleActions}

      <Spacer />

      <RightGroup>
        {filterPill}
        {leftToolbarExtras}
        <SortPill sorting={sorting} setSorting={setSorting} columns={columns} excludeSortKeys={excludeSortKeys} />
        {(toolbarActions?.length > 0 || toolbarExtras) && (
          <TableToolbar actions={toolbarActions || []}>{toolbarExtras}</TableToolbar>
        )}
      </RightGroup>
    </Row>
  );
};

export default TableHeader;
